// Employee support pathway view — Employee Portal & App spec §9.3 + §9.4.
//
// A SOFT view of the 12-month support pathway. The spec is emphatic about tone:
// this must read as reassurance that the organisation will remember important
// moments, NOT as a case-management timeline. §9.3.1 wording — Use: "Support
// pathway / Support moments / Touch points / You can update your preferences at
// any time / Nothing here forces you to have a conversation." Avoid: "Case /
// Compliance / Monitoring / Required check-ins / Progress tracking." So no
// internal overdue flags (§9.4.2), no operational "dispatched/scheduled"
// states — only the gentle Upcoming / Paused / Completed distinction.
//
// §9.4.3 — for each touch point the employee can adjust a preference (who
// contacts them, channel, pause/move/sooner, team-sharing). `request='not_now'`
// pauses the touch point. The employee can hide/minimise the whole pathway if
// they do not want to look at it.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TOUCHPOINT_PREF_CONTACT_PERSONS,
  TOUCHPOINT_PREF_REQUESTS,
  type SetTouchpointPreferenceRequest,
  type TouchpointSummary,
  type TouchpointType,
} from '@besideu/shared/cases';
import {
  employeeFacingStatus,
  isTouchpointPreferenceLocked,
} from '@besideu/shared/touchpoint-status';
import { api } from '@/lib/api.ts';
import { Button } from '@/components/ui/Button.tsx';
import { Card } from '@/components/ui/Card.tsx';

// Friendly label + a gentle, employee-facing purpose for each support moment.
// Purposes are the §9.3.2 "Employee-facing purpose" column, softened.
const SUPPORT_MOMENTS: Record<TouchpointType, { label: string; purpose: string }> = {
  Day_3: {
    label: 'Early on',
    purpose:
      'A gentle opportunity to confirm practical needs or how you would like to be contacted.',
  },
  Week_1: {
    label: 'First week',
    purpose: 'A chance to understand leave, entitlements and the support available to you.',
  },
  Pre_Return: {
    label: 'Before returning',
    purpose: 'Support with how returning to work could be handled, whenever feels right for you.',
  },
  Week_1_Back: {
    label: 'First week back',
    purpose: 'A gentle point of contact during your first days back, if you would find it helpful.',
  },
  Month_1: {
    label: 'Around a month',
    purpose: 'A chance to look at workload, flexibility or any ongoing support.',
  },
  Month_3: {
    label: 'Around three months',
    purpose: 'Support does not have to disappear after the first few weeks.',
  },
  Month_6: {
    label: 'Around six months',
    purpose: 'A quiet reminder that grief can still affect work and wellbeing months later.',
  },
  Month_9: {
    label: 'Around nine months',
    purpose: 'Light-touch support, here if you want it.',
  },
  Month_12: {
    label: 'Around a year',
    purpose: 'A recognition that the first anniversary may matter.',
  },
  Anniversary: {
    label: 'Around significant dates',
    purpose: 'Support around dates that may feel particularly difficult.',
  },
};

const CONTACT_PERSON_LABELS = { manager: 'My manager', hr: 'HR' } as const;
const REQUEST_LABELS = {
  not_now: 'I would prefer not to be contacted right now',
  move_later: 'I would like to move this touch point',
  sooner: 'I would like to speak to someone sooner',
} as const;

export function SupportPathwayView({
  touchpoints,
  hasCase,
  onPrepareConversation,
}: {
  touchpoints: TouchpointSummary[];
  hasCase: boolean;
  // Support Framework §15 — when provided, each support moment offers an
  // opt-in "talk to Maeve about this" affordance that opens the Companion with
  // a gentle opener seeded (never sent). Omitted on surfaces without a Maeve
  // section (e.g. the mobile Maeve-First layout already centres the chat).
  onPrepareConversation?: ((momentLabel: string) => void) | undefined;
}) {
  const [hidden, setHidden] = useState(false);

  return (
    <Card as="section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Your support pathway</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This is here so your organisation can offer support at the right times. Nothing here
            asks you to do anything, and you can update your preferences at any time.
          </p>
        </div>
        <Button variant="ghost" type="button" onClick={() => setHidden((h) => !h)}>
          {hidden ? 'Show' : 'Hide'}
        </Button>
      </div>

      {hidden ? null : (
        <div className="mt-5">
          {!hasCase || touchpoints.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              When support moments are planned, they will appear here as a gentle guide — never as
              something you have to act on.
            </p>
          ) : (
            <ol className="space-y-3">
              {touchpoints.map((t) => (
                <TouchpointRow
                  key={t.id}
                  touchpoint={t}
                  onPrepareConversation={onPrepareConversation}
                />
              ))}
            </ol>
          )}
        </div>
      )}
    </Card>
  );
}

// §7 employee-facing wording — single source of truth in
// `@besideu/shared/touchpoint-status`. This used to be a hand-rolled
// subset here (completed / paused / else-"Upcoming") that never checked
// `scheduledFor` against now, so an actually-overdue touch point still
// read "Upcoming" instead of the spec's "Available now".
function statusLabel(t: TouchpointSummary): string {
  return employeeFacingStatus(t.status, new Date(t.scheduledFor), new Date());
}

function TouchpointRow({
  touchpoint: t,
  onPrepareConversation,
}: {
  touchpoint: TouchpointSummary;
  onPrepareConversation?: ((momentLabel: string) => void) | undefined;
}) {
  const moment = SUPPORT_MOMENTS[t.type];
  // `status` only flips to `Suppressed` (→ "Paused for now") when it was
  // `Pending` at save time (`setTouchpointPreference`, `services/touchpoints.ts`)
  // — a touch point already `HR_Review_Needed`/etc. keeps its real status even
  // after the employee sets `not_now`, so `statusLabel` alone can miss it.
  // Check the employee's own saved preference directly too, so their explicit
  // "don't contact me" is never silently dropped from the paused-gated UI
  // (hiding "Talk to Maeve", the "Updated ·" prefix) regardless of whether the
  // backend also moved the status column.
  const paused = statusLabel(t) === 'Paused for now' || t.preference?.request === 'not_now';
  return (
    <li className="rounded-md border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="font-medium">{moment.label}</p>
        <span className="text-xs text-muted-foreground">
          {/* §7 employee-facing "Updated" when the moment's timing has changed. */}
          {t.wasRescheduled && !paused ? 'Updated · ' : ''}
          {statusLabel(t)} · {t.scheduledFor}
        </span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{moment.purpose}</p>

      {/* §15 — opt-in "prepare for a conversation". Soft, low-pressure; opens
          Maeve with an opener you can edit or ignore. Hidden for paused moments
          (the employee asked for space) and when no Maeve section is available. */}
      {onPrepareConversation && !paused ? (
        <button
          type="button"
          className="mt-3 text-sm text-primary underline-offset-4 hover:underline"
          onClick={() => onPrepareConversation(moment.label)}
        >
          Talk to Maeve about this — only if you’d like to
        </button>
      ) : null}

      {/* §9.4.3 — optional per-touch-point adjustments, collapsed by default.
          Hidden once the touch point has actually finished (Completed /
          Done_Informally / Skipped) — the server rejects a preference write
          in that state too (`isTouchpointPreferenceLocked`), so this is UX,
          not the enforcement; without it, an employee could open the form on
          an already-Completed touch point and set `not_now`, producing a
          "Completed" row that simultaneously behaved as paused. */}
      {isTouchpointPreferenceLocked(t.status) ? null : (
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-primary">Adjust this touch point</summary>
          <TouchpointPreferenceForm touchpoint={t} />
        </details>
      )}
    </li>
  );
}

function TouchpointPreferenceForm({ touchpoint: t }: { touchpoint: TouchpointSummary }) {
  const queryClient = useQueryClient();
  const p = t.preference;
  const [form, setForm] = useState<SetTouchpointPreferenceRequest>({
    contactPerson: p?.contactPerson ?? null,
    preferEmail: p?.preferEmail ?? false,
    request: p?.request ?? null,
    doNotShareWithTeam: p?.doNotShareWithTeam ?? false,
  });

  const mutation = useMutation({
    mutationFn: (input: SetTouchpointPreferenceRequest) =>
      api.post<{ ok: true }>(`/api/employee/touchpoints/${t.id}/preference`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['employee', 'portal'] }),
  });

  const set = (patch: Partial<SetTouchpointPreferenceRequest>) =>
    setForm((f) => ({ ...f, ...patch }));

  return (
    <form
      className="mt-3 space-y-4 border-t border-border pt-3"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      <fieldset>
        <legend className="text-sm font-medium">Who would you like to contact you?</legend>
        <div className="mt-2 space-y-1.5">
          {TOUCHPOINT_PREF_CONTACT_PERSONS.map((person) => (
            <label key={person} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={`person-${t.id}`}
                checked={form.contactPerson === person}
                onChange={() => set({ contactPerson: person })}
              />
              {CONTACT_PERSON_LABELS[person]}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`person-${t.id}`}
              checked={form.contactPerson === null}
              onChange={() => set({ contactPerson: null })}
            />
            No preference
          </label>
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.preferEmail ?? false}
          onChange={(e) => set({ preferEmail: e.target.checked })}
        />
        I would prefer email
      </label>

      <fieldset>
        <legend className="text-sm font-medium">Right now</legend>
        <div className="mt-2 space-y-1.5">
          {TOUCHPOINT_PREF_REQUESTS.map((request) => (
            <label key={request} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name={`request-${t.id}`}
                checked={form.request === request}
                onChange={() => set({ request })}
              />
              {REQUEST_LABELS[request]}
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`request-${t.id}`}
              checked={form.request === null}
              onChange={() => set({ request: null })}
            />
            No change
          </label>
        </div>
      </fieldset>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.doNotShareWithTeam ?? false}
          onChange={(e) => set({ doNotShareWithTeam: e.target.checked })}
        />
        I do not want this shared with my wider team
      </label>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {mutation.isSuccess && !mutation.isPending && (
          <span className="text-sm text-muted-foreground" role="status">
            Saved.
          </span>
        )}
        {mutation.isError && (
          <span className="text-sm text-traffic-red" role="alert">
            Could not save. Try again.
          </span>
        )}
      </div>
    </form>
  );
}
